<template>
  <div class="container">
    <div class="header">
      <h1>军校报考150问</h1>
      <p>
        全面解答军校报考过程中的各类问题，帮助考生顺利完成报考流程，实现军旅梦想
      </p>
    </div>

    <div v-if="loading" class="empty-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>加载数据中...</p>
    </div>

    <div v-else-if="error" class="empty-state">
      <i class="fas fa-exclamation-circle"></i>
      <p>数据加载失败，请重试</p>
      <button @click="fetchData" class="el-button el-button-primary">
        重试
      </button>
    </div>

    <div v-else class="categories-container">
      <div
        v-for="category in categories"
        :key="category.id"
        class="category-card"
      >
        <div class="category-header">
          <div class="category-title">
            <i
              :class="'fas fa-' + getIcon(category.title) + ' category-icon'"
            ></i>
            <span>{{ category.title }}</span>
          </div>
          <div class="view-more" @click="viewMore(category.title)">
            查看更多 <i class="fas fa-chevron-right"></i>
          </div>
        </div>
        <div class="questions-list">
          <div
            v-for="(question, index) in category.questions"
            :key="question.id"
            class="question-item"
            @click="goToQuestionDetail(question.id)"
          >
            <div class="question-index">{{ index + 1 }}</div>
            <div class="question-text">{{ question.title }}</div>
          </div>
        </div>
        <div class="stats-bar">
          <div class="question-count">
            <i class="fas fa-question-circle"></i>
            {{ category.questions.length }}个问题
          </div>
          <div>最近更新: {{ formatDate(category.updated) }}</div>
        </div>
      </div>
    </div>..
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import axios from "axios";
import { baseURL } from "@/api/axiosConfig";
import { useRouter } from "vue-router"; // 引入 useRouter

export default {
  name: "MilitarySchoolExam",
  setup() {
    const router = useRouter(); // 获取路由实例
    const categories = ref([]);
    const loading = ref(true);
    const error = ref(false);

    // 模拟分类与图标映射
    const categoryIcons = {
      志愿填报: "file-alt",
      报考须知: "info-circle",
      身体检查: "heartbeat",
      身心检查: "heartbeat",
      政审面试: "user-shield",
      入学和发展: "graduation-cap",
      飞行员招考: "fighter-jet",
      其它: "bars",
    };

    // 获取分类对应的图标
    const getIcon = (title) => {
      return categoryIcons[title] || "question";
    };

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return "未知";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    };

    // 从后台获取数据
    const fetchData = async () => {
      loading.value = true;
      error.value = false;

      try {
        const response = await axios.get(baseURL + "/interface/news/newslist");

        if (response.data.code === 200) {
          // 确保数据存在且是数组
          if (response.data.data && Array.isArray(response.data.data)) {
            processBackendData(response.data.data);
          } else {
            throw new Error("数据格式不正确");
          }
        } else {
          throw new Error(response.data.msg || "获取数据失败");
        }
      } catch (err) {
        console.error("获取数据失败", err);
        error.value = true;
      } finally {
        loading.value = false;
      }
    };

    // 处理后台返回的数据结构
    const processBackendData = (data) => {
      const result = [];

      if (!data || !Array.isArray(data)) {
        categories.value = result;
        return;
      }

      data.forEach((categoryData) => {
        if (categoryData && categoryData.level === 0) {
          // 一级分类
          result.push({
            id: categoryData.id,
            name: categoryData.title,
            title: categoryData.title,
            icon: getIcon(categoryData.title),
            questions: categoryData.child || [],
            count: (categoryData.child || []).length,
            updated: new Date().toISOString().split("T")[0], // 模拟更新日期
          });
        }
      });

      categories.value = result;
    };

    // 查看更多问题
    const viewMore = (categoryTitle) => {
      router.push({
        path: "/questions2",
        query: { belong: categoryTitle },
      });
    };

    // 跳转到问题详情页
    const goToQuestionDetail = (id) => {
      router.push({
        path: `/news/detail/${id}`,
        query: { source: 'questions' },
      });
    };

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData();
    });

    return {
      categories,
      loading,
      error,
      fetchData,
      viewMore,
      goToQuestionDetail,
      getIcon,
      formatDate,
    };
  },
};
</script>

<style scoped>
/* 原有样式保持不变 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Microsoft YaHei", "Segoe UI", sans-serif;
}

body {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4edf9 100%);
  min-height: 100vh;
  padding: 20px;
}

.container {
  width: calc(100% - 20px);
  max-width: 1440px;
  margin: 80px auto;
  padding: 0 10px;
  box-sizing: border-box;
}

.header {
  text-align: center;
  padding: 30px 0;
  position: relative;
}

.header h1 {
  font-size: 2.5rem;
  color: #2c5cc5;
  margin-bottom: 10px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  display: inline-block;
}

.header p {
  color: #5a6a8c;
  font-size: 1.1rem;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.categories-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 20px;
}

.category-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #eaeff5;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(41, 98, 255, 0.15);
}

.category-header {
  background: #4c8aff;
  color: white;
  padding: 20px 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-title {
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  font-weight: 600;
}

.category-icon {
  margin-right: 12px;
  font-size: 1.4rem;
}

.view-more {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.view-more:hover {
  color: white;
  transform: translateX(3px);
}

.view-more i {
  margin-left: 5px;
  font-size: 0.9rem;
}

.questions-list {
  padding: 20px;
}

.question-item {
  padding: 15px 0;
  border-bottom: 1px solid #f0f3f7;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
}

.question-item:hover {
  background-color: #f8faff;
  border-radius: 8px;
  padding-left: 10px;
}

.question-item:last-child {
  border-bottom: none;
}

.question-index {
  min-width: 28px;
  height: 28px;
  background: #eef5ff;
  color: #2c5cc5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 15px;
  flex-shrink: 0;
}

.question-text {
  flex: 1;
  color: #3a4a6a;
  font-size: 1.05rem;
}

.question-item:hover .question-text {
  color: #1a3a7c;
  font-weight: 500;
}

.stats-bar {
  display: flex;
  justify-content: space-between;
  background: #f8faff;
  padding: 15px 20px;
  border-top: 1px solid #edf2ff;
  color: #5a6a8c;
  font-size: 0.9rem;
}

.question-count {
  display: flex;
  align-items: center;
}

.question-count i {
  margin-right: 5px;
  color: #2c5cc5;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #a3b1c8;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .categories-container {
    grid-template-columns: 1fr;
  }

  .header h1 {
    font-size: 2rem;
  }

  .header p {
    font-size: 1rem;
  }
}
</style>
